import { useState } from 'react'
import { Search, Calendar, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { DateRangePicker } from '@/components/DateRangePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ORDER_STATUS_FLOW, ORDER_STATUS_LABELS, PAY_TYPE_LABELS } from '@/schemas/supplier'
import type { OrderListParams } from '@/schemas/supplier'

interface OrderFilterBarProps {
  params: OrderListParams
  onParamsChange: (params: Partial<OrderListParams>) => void
}

// 状态选项按业务流程排序（ORDER_STATUS_FLOW，排除已删除）
const statusOptions = [
  { value: '', label: '全部状态' },
  ...ORDER_STATUS_FLOW.map((status) => ({ value: String(status), label: ORDER_STATUS_LABELS[status] })),
]

// 支付方式选项
const payTypeOptions = [
  { value: 'all', label: '全部支付方式' },
  ...Object.entries(PAY_TYPE_LABELS).map(([value, label]) => ({ value, label })),
]

export function OrderFilterBar({ params, onParamsChange }: OrderFilterBarProps) {
  const [showDatePicker, setShowDatePicker] = useState(false)

  const handleDateChange = (value: { start_date?: string; end_date?: string }) => {
    if (value.start_date && value.end_date) {
      onParamsChange({
        data: `${value.start_date} - ${value.end_date}`
      })
    } else {
      onParamsChange({ data: undefined })
    }
  }

  const clearDateFilter = () => {
    onParamsChange({ data: undefined })
    setShowDatePicker(false)
  }

  const clearAllFilters = () => {
    onParamsChange({
      status: undefined,
      real_name: undefined,
      data: undefined,
      pay_type: undefined,
    })
  }

  const hasActiveFilters = params.status || params.real_name || params.data || params.pay_type

  // Parse current date value for DateRangePicker
  const dateValue = params.data
    ? { start_date: params.data.split(' - ')[0], end_date: params.data.split(' - ')[1] }
    : undefined

  return (
    <div className="space-y-4">
      {/* 状态 Tab */}
      <div className="flex flex-wrap gap-2">
        {statusOptions.map((option) => (
          <Badge
            key={option.value}
            variant={params.status === option.value ? 'default' : 'outline'}
            className="cursor-pointer hover:bg-primary/80"
            onClick={() => onParamsChange({ status: option.value || undefined })}
          >
            {option.label}
          </Badge>
        ))}
      </div>

      {/* 搜索和筛选 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* 搜索框 */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜索收货人姓名/手机号..."
            value={params.real_name || ''}
            onChange={(e) => onParamsChange({ real_name: e.target.value || undefined })}
            className="pl-9"
          />
        </div>

        {/* 支付方式筛选 */}
        <Select
          value={params.pay_type || 'all'}
          onValueChange={(value) => onParamsChange({ pay_type: value === 'all' ? undefined : value })}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="支付方式" />
          </SelectTrigger>
          <SelectContent>
            {payTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* 日期筛选 */}
        <div className="flex items-center gap-2">
          {showDatePicker || params.data ? (
            <div className="flex items-center gap-2">
              <DateRangePicker
                value={dateValue}
                onChange={handleDateChange}
              />
              {params.data && (
                <Button variant="ghost" size="icon" onClick={clearDateFilter}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDatePicker(true)}
            >
              <Calendar className="mr-1.5 h-4 w-4" />
              日期筛选
            </Button>
          )}
        </div>

        {/* 清除筛选 */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1.5 h-4 w-4" />
            清除筛选
          </Button>
        )}
      </div>
    </div>
  )
}
